import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from '@modules/common/components/modal';
import { InputTextField } from '@modules/business/components/index';
import { ButtonPrimary } from '@modules/common/components/form/buttons/button-primary';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { toaster } from '@lib/util/toaster';

type Props = {
    textColor?: string;
    title?: string;
};

const DeliveryDate = (props: Props) => {
    const form = useFormikContext();
    const [deliveryDateText, setDeliveryDateText] = useState('');
    const [deliveryDateState, setDeliveryDateState] = useState<Date | null>();
    const [deliveryTimeState, setDeliveryTimeState] = useState<Date | null>(new Date(moment().format('YYYY-MM-DD') + ' 00:00'));
    const [showDatePicker, setShowDatePicker] = useState(false);
    const { title } = props;
    const deliveryDateFieldName = 'deliveryDate';
    const afterCheckoutText = 'after checkout.';

    const todayDate = () => {
        return new Date(
            Date.UTC(
                new Date().getFullYear(),
                new Date().getMonth(),
                new Date().getDate(),
                new Date().getHours(),
                new Date().getMinutes(),
            ),
        );
    };

    const handleChooseDate = () => {
        if (deliveryDateState && deliveryTimeState) {
            const dateChoose = new Date(
                Date.UTC(
                    deliveryDateState.getFullYear(),
                    deliveryDateState.getMonth(),
                    deliveryDateState.getDate(),
                    deliveryTimeState.getHours(),
                    deliveryTimeState.getMinutes(),
                ),
            );
            let utcDate = JSON.stringify(dateChoose);
            if (moment(dateChoose).isBefore(moment(todayDate()))) {
                return toaster.error('Please select a time in the future.');
            }
            form?.setFieldValue(
                deliveryDateFieldName,
                utcDate.substring(1, utcDate.length - 1),
            );
        } else {
            form?.setFieldValue(deliveryDateFieldName, '');
        }
        setShowDatePicker(!showDatePicker);
        setDeliveryDateText(getDeliveryText);
    };

    const getDeliveryText = () => {
        if (!deliveryDateState || !deliveryTimeState) return afterCheckoutText;

        let selectedDate = JSON.stringify(
            new Date(
                Date.UTC(
                    deliveryDateState.getFullYear(),
                    deliveryDateState.getMonth(),
                    deliveryDateState.getDate(),
                    deliveryTimeState.getHours(),
                    deliveryTimeState.getMinutes(),
                ),
            ),
        );

        const curDate = JSON.stringify(todayDate);
        if (selectedDate === curDate) {
            form?.setFieldValue(deliveryDateFieldName, '');
            return afterCheckoutText;
        }

        let deliveryText = deliveryDateState?.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
        const timeText = deliveryTimeState?.toLocaleTimeString('en-US');
        const temp = deliveryText.split(',');
        temp[0] = 'on ' + timeText + ' ' + temp[0];
        return `on ${timeText} ${moment(deliveryDateState).format('MMMM Do, YYYY')}`;
    };

    useEffect(() => {
        form?.setFieldValue(deliveryDateFieldName, '');
        setDeliveryDateText(getDeliveryText);
    }, []);

    return (
        <div className="text-left font-medium text-base items-center pt-1">
            {deliveryDateText !== afterCheckoutText && (
                <div className="text-gray-ada">
                    <b>
                        <i>Prepaid Gift card is set to deliver {deliveryDateText}</i>
                    </b>{' '}
                    <br />
                </div>
            )}
            {deliveryDateText === afterCheckoutText && <span>Deliver Now</span>} ({''}
            <span
                className="hover:cursor-pointer text-haloween_orange"
                onClick={() => {
                    setShowDatePicker(!showDatePicker);
                }}
            >
        {title}
      </span>
            )
            <InputTextField
                label={'Delivery Date'}
                name={'deliveryDate'}
                hidden={true}
                autocomplete="off"
                type="text"
            />
            <div className="max-w-[550px] mx-auto text-gray-ada pt-2">
                {deliveryDateText === afterCheckoutText && (
                    <div>
                        <b>
                            <i>Gift card is set to deliver {deliveryDateText}</i>
                        </b>
                        <br />
                    </div>
                )}
                Most approved eGift orders are delivered within 15 minutes, but may take
                up to 24 hours. You can also set a delivery date in the future.
            </div>
            <Modal
                isOpen={showDatePicker}
                close={() => setShowDatePicker(!showDatePicker)}
                size={'small'}
            >
                <Modal.Title>Set Delivery Date</Modal.Title>
                <Modal.Body className="flex items-end">
                    <div
                        className="flex flex-col w-full justify-items-center justify-center py-8 border-t border-gray-300">
                        <div className="w-full customer-datepicker">
                            <DatePicker
                                selected={deliveryDateState}
                                minDate={new Date()}
                                onChange={(date) => setDeliveryDateState(date)}
                                inline
                            />
                        </div>
                        <div className="set-delivery-time flex gap-2 items-center">
                            <span className="text-base flex-none w-[7rem]">Choose time</span>
                            <DatePicker
                                selected={deliveryTimeState}
                                onChange={(date) => {
                                    setDeliveryTimeState(date);
                                    if (!deliveryDateState) setDeliveryDateState(new Date());
                                }}
                                showTimeSelect
                                showTimeSelectOnly
                                dateFormat="h:mm aa"
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex flex-col w-full justify-items-center justify-center">
                        <div className="min-w-[250px] mx-auto">
                            <ButtonPrimary
                                size="medium"
                                type="button"
                                onClick={handleChooseDate}
                                variant="primary"
                                className="min-w-[250px] text-base"
                            >
                                Choose Date & Time
                            </ButtonPrimary>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DeliveryDate;
